import React from 'react';

// Generovaná topografická mapa (vrstevnice) pomocou "marching squares"
// nad syntetickým výškovým poľom (súčet sínusoviek rôznych smerov a frekvencií).
// Všetko sa počíta raz pri načítaní modulu – deterministicky, žiadny obrázok.

const WIDTH = 1000;
const HEIGHT = 700;
const CELL = 11;
const COLS = Math.ceil(WIDTH / CELL);
const ROWS = Math.ceil(HEIGHT / CELL);
const LEVELS = 30;
const ACCENT_LEVEL_INDEX = 19; // ktorá vrstevnica sa vykreslí ako zvýraznená (červená)

/** Syntetické "terénne" výškové pole – súčet niekoľkých sínusoviek s rôznym smerom a mierkou. */
function heightAt(x, y) {
  return (
    Math.sin(x * 0.021 + y * 0.017) +
    Math.sin(x * 0.013 - y * 0.027 + 1.7) * 0.75 +
    Math.sin((x + y) * 0.01 + 3.1) * 0.55 +
    Math.sin(x * 0.037 - y * 0.012 + 5.2) * 0.35 +
    Math.sin(y * 0.041 + x * 0.006 + 2.4) * 0.3
  );
}

function lerpFraction(level, v1, v2) {
  if (v2 === v1) return 0.5;
  return (level - v1) / (v2 - v1);
}

/** Vypočíta výšku vo všetkých bodoch mriežky (uzly buniek). */
function buildHeightGrid() {
  const grid = [];
  for (let row = 0; row <= ROWS; row += 1) {
    const line = new Float32Array(COLS + 1);
    for (let col = 0; col <= COLS; col += 1) {
      line[col] = heightAt(col * CELL, row * CELL);
    }
    grid.push(line);
  }
  return grid;
}

/** Marching squares: pre danú hladinu vráti SVG path „d" so všetkými segmentmi vrstevnice. */
function buildContourPath(grid, level) {
  const points = [];

  for (let row = 0; row < ROWS; row += 1) {
    const top = grid[row];
    const bottom = grid[row + 1];

    for (let col = 0; col < COLS; col += 1) {
      const a = top[col]; // NW
      const b = top[col + 1]; // NE
      const c = bottom[col + 1]; // SE
      const dd = bottom[col]; // SW

      const state = (a > level ? 8 : 0) | (b > level ? 4 : 0) | (c > level ? 2 : 0) | (dd > level ? 1 : 0);
      if (state === 0 || state === 15) continue;

      const x0 = col * CELL;
      const y0 = row * CELL;

      const topPt = [x0 + lerpFraction(level, a, b) * CELL, y0];
      const rightPt = [x0 + CELL, y0 + lerpFraction(level, b, c) * CELL];
      const bottomPt = [x0 + lerpFraction(level, dd, c) * CELL, y0 + CELL];
      const leftPt = [x0, y0 + lerpFraction(level, a, dd) * CELL];

      // Každá dvojica bodov v poli = jeden úsek vrstevnice (M..L..).
      switch (state) {
        case 1:
          points.push(leftPt, bottomPt);
          break;
        case 2:
          points.push(bottomPt, rightPt);
          break;
        case 3:
          points.push(leftPt, rightPt);
          break;
        case 4:
          points.push(topPt, rightPt);
          break;
        case 5:
          points.push(topPt, leftPt, bottomPt, rightPt);
          break;
        case 6:
          points.push(topPt, bottomPt);
          break;
        case 7:
          points.push(topPt, leftPt);
          break;
        case 8:
          points.push(topPt, leftPt);
          break;
        case 9:
          points.push(topPt, bottomPt);
          break;
        case 10:
          points.push(topPt, rightPt, leftPt, bottomPt);
          break;
        case 11:
          points.push(topPt, rightPt);
          break;
        case 12:
          points.push(leftPt, rightPt);
          break;
        case 13:
          points.push(bottomPt, rightPt);
          break;
        case 14:
          points.push(leftPt, bottomPt);
          break;
        default:
          break;
      }
    }
  }

  let d = '';
  for (let i = 0; i < points.length; i += 2) {
    const p1 = points[i];
    const p2 = points[i + 1];
    d += `M${p1[0].toFixed(1)} ${p1[1].toFixed(1)} L${p2[0].toFixed(1)} ${p2[1].toFixed(1)} `;
  }
  return d;
}

const heightGrid = buildHeightGrid();
const levelPaths = Array.from({ length: LEVELS }, (_, index) => {
  const level = -2.3 + (index / (LEVELS - 1)) * 4.6;
  return buildContourPath(heightGrid, level);
});

/** Dekoratívne pozadie v štýle vrstevníc topografickej mapy. */
export default function TopoBackground() {
  return (
    <svg viewBox={`0 0 ${WIDTH} ${HEIGHT}`} preserveAspectRatio="xMidYMid slice" focusable="false">
      {levelPaths.map((d, index) =>
        index === ACCENT_LEVEL_INDEX ? null : (
          <path key={index} d={d} fill="none" stroke="var(--ink)" strokeWidth={0.6} />
        )
      )}
      <path d={levelPaths[ACCENT_LEVEL_INDEX]} fill="none" stroke="var(--red)" strokeWidth={2.4} strokeLinecap="round" />
    </svg>
  );
}
